<template>
  <div v-if="tradition" class="culture-page">
    <CultureNav />
    <CultureHero
      :title="tradition.name"
      :summary="tradition.summary"
      :eyebrow="tradition.eyebrow"
      :status="tradition.status"
      :image="heroImage"
    >
      <CultureTagList :tags="tradition.tags" />
    </CultureHero>
    <main>
      <v-container>
        <section class="detail-grid">
          <div>
            <p class="kicker">Why It Matters</p>
            <h2>The Tradition</h2>
            <p class="description">{{ tradition.description || tradition.summary }}</p>
          </div>
          <dl>
            <div v-for="stat in stats" :key="stat.label">
              <dt>{{ stat.label }}</dt><dd>{{ stat.value }}</dd>
            </div>
          </dl>
        </section>

        <TraditionProgressBar
          :count="tradition.occurrenceCount"
          :milestone="tradition.nextMilestone"
          :unit="tradition.cadence === 'weekly' ? 'Fridays' : 'occurrences'"
        />

        <section v-if="tradition.storySections && tradition.storySections.length" class="section-block story-block">
          <p class="kicker">The Reckon Connection</p>
          <h2>The Story</h2>
          <article v-for="section in tradition.storySections" :key="section.heading" class="story-section">
            <h3>{{ section.heading }}</h3>
            <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </article>
        </section>

        <section v-if="tradition.milestones && tradition.milestones.length" class="section-block">
          <p class="kicker">The Living Count</p>
          <h2>Milestones</h2>
          <MilestoneList
            :milestones="tradition.milestones"
            :current-count="tradition.occurrenceCount"
          />
        </section>

        <section
          v-if="tradition.confirmationNotes && tradition.confirmationNotes.length"
          class="section-block confirmation-block"
        >
          <div>
            <v-icon>mdi-alert-circle-outline</v-icon>
          </div>
          <div>
            <p class="kicker">Archive Notes</p>
            <h2>What Still Needs Confirmation</h2>
            <ul>
              <li v-for="note in tradition.confirmationNotes" :key="note">{{ note }}</li>
            </ul>
          </div>
        </section>

        <section v-if="sortedOccurrences.length" class="section-block">
          <p class="kicker">{{ tradition.occurrenceSectionKicker || 'Year by Year' }}</p>
          <h2>{{ tradition.occurrenceSectionTitle || 'Occurrences' }}</h2>
          <div v-for="group in occurrenceGroups" :key="group.year" class="occurrence-group">
            <h3>{{ group.year }}</h3>
            <div class="occurrence-grid">
              <OccurrenceCard
                v-for="item in group.items"
                :key="item.id"
                :occurrence="item"
              />
            </div>
          </div>
        </section>

        <AttendanceBoard
          v-if="tradition.id === 'chick-fil-a-friday'"
          :records="tradition.attendance || []"
        />

        <section v-if="tradition.locations && tradition.locations.length" class="section-block">
          <p class="kicker">Where We Gather</p>
          <h2>Our Most Common Stops</h2>
          <div class="location-grid">
            <a
              v-for="location in tradition.locations"
              :key="location.id"
              :href="location.websiteUrl"
              class="location-card"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div>
                <p>{{ [location.city, location.state].filter(Boolean).join(', ') }}</p>
                <h3>{{ location.name }}</h3>
                <span>{{ location.description }}</span>
              </div>
              <strong>Visit Website <v-icon small>mdi-open-in-new</v-icon></strong>
            </a>
          </div>
        </section>

        <section
          v-else-if="tradition.cadence === 'recurring'
            && !sortedOccurrences.length
            && !(tradition.locations && tradition.locations.length)"
          class="section-block empty-occurrences"
        >
          <p class="kicker">{{ tradition.occurrenceSectionKicker || 'A Living Series' }}</p>
          <h2>{{ tradition.occurrenceSectionTitle || 'Occurrences' }}</h2>
          <p>{{ tradition.emptyOccurrencesCopy || 'Individual gatherings can be added here as lightweight cards, grouped by year, without building a separate page for every stop.' }}</p>
        </section>

        <section
          v-if="tradition.meritBadgeIds && tradition.meritBadgeIds.length"
          class="section-block"
        >
          <p class="kicker">Passport Ready</p>
          <h2>Related Merit Badge</h2>
          <div class="badge-card">
            <v-icon>mdi-medal-outline</v-icon>
            <strong>{{ badgeLabel(tradition.meritBadgeIds[0]) }}</strong>
            <span>Automated awarding is coming later.</span>
          </div>
        </section>

        <section v-if="tradition.gallery && tradition.gallery.length" class="section-block">
          <p class="kicker">Together Over Time</p>
          <h2>Gallery</h2>
          <CultureGallery :images="tradition.gallery" />
        </section>

        <section class="section-block">
          <p class="kicker">Keep Exploring</p>
          <h2>Related Traditions</h2>
          <div class="related-grid">
            <CultureCard
              v-for="item in relatedTraditions"
              :key="item.id"
              :title="item.name"
              :summary="item.summary"
              :eyebrow="item.eyebrow"
              :image="item.heroImage"
              :href="relatedRoute(item)"
            />
          </div>
        </section>
      </v-container>
    </main>
    <AppFooter />
  </div>
  <div v-else class="not-found">
    <v-container>
      <h1>Tradition not found</h1>
      <v-btn :to="{ name: 'reckon-way-traditions' }">View Traditions</v-btn>
    </v-container>
  </div>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';
import AppFooter from '@/components/Footer.vue';
import CultureNav from '@/components/reckon-way/culture/CultureNav.vue';
import CultureHero from '@/components/reckon-way/culture/CultureHero.vue';
import CultureTagList from '@/components/reckon-way/culture/CultureTagList.vue';
import TraditionProgressBar from '@/components/reckon-way/culture/TraditionProgressBar.vue';
import MilestoneList from '@/components/reckon-way/culture/MilestoneList.vue';
import OccurrenceCard from '@/components/reckon-way/culture/OccurrenceCard.vue';
import CultureCard from '@/components/reckon-way/culture/CultureCard.vue';
import CultureGallery from '@/components/reckon-way/culture/CultureGallery.vue';
import AttendanceBoard from '@/components/reckon-way/culture/AttendanceBoard.vue';
import { cadenceLabels, formatCultureDate } from '@/content/culture';
import { traditions, traditionsBySlug } from '@/content/culture/traditions';

const fallbackImage = mediaRef('adventures/the-reckon-way-hero.png');

export default {
  name: 'TraditionDetailView',
  components: {
    AppFooter,
    CultureNav,
    CultureHero,
    CultureTagList,
    TraditionProgressBar,
    MilestoneList,
    OccurrenceCard,
    CultureCard,
    CultureGallery,
    AttendanceBoard,
  },
  data: () => ({ fallbackImage }),
  computed: {
    tradition() { return traditionsBySlug[this.$route.params.traditionSlug]; },
    heroImage() {
      return this.tradition.heroImage
        || (this.tradition.gallery || []).find((image) => image.featured)?.src
        || this.tradition.defaultPlace?.image
        || this.fallbackImage;
    },
    stats() {
      if (!this.tradition) return [];
      const precision = this.tradition.id === 'anniversary-dinner' ? 'month' : 'day';
      return [
        { label: 'Started', value: this.tradition.startDateLabel || (this.tradition.startDate ? formatCultureDate(this.tradition.startDate, precision) : '') },
        { label: 'Frequency', value: cadenceLabels[this.tradition.cadence] },
        { label: 'Occurrences', value: this.tradition.occurrenceCount },
        { label: 'Next Milestone', value: this.tradition.nextMilestone },
      ].filter((item) => item.value);
    },
    sortedOccurrences() {
      return [...(this.tradition?.occurrences || [])]
        .sort((a, b) => b.date.localeCompare(a.date));
    },
    occurrenceGroups() {
      return this.sortedOccurrences.reduce((groups, item) => {
        const year = item.date.slice(0, 4) || 'Undated';
        const group = groups.find((entry) => entry.year === year);
        if (group) group.items.push(item);
        else groups.push({ year, items: [item] });
        return groups;
      }, []);
    },
    relatedTraditions() {
      return traditions.filter((item) => item.id !== this.tradition.id).slice(0, 3);
    },
  },
  methods: {
    relatedRoute(item) {
      return item.archiveRouteName
        ? { name: item.archiveRouteName }
        : { name: 'reckon-way-tradition', params: { traditionSlug: item.slug } };
    },
    badgeLabel(value) {
      return value.split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
    },
  },
};
</script>

<style scoped>
.culture-page, main, .not-found { background: #050505; }
.culture-page main { padding: 64px 0 76px; }
.detail-grid { display: grid; gap: 36px; grid-template-columns: minmax(0, 1.4fr) minmax(260px, .6fr); }
.kicker { color: #e23232; font-family: "Oswald", sans-serif; font-size: .8rem; font-weight: 900; margin: 0 0 7px; text-transform: uppercase; }
h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: 2.4rem; margin: 0 0 18px; }
.story-section { max-width: 820px; }
.story-section + .story-section { border-top: 1px solid #242424; margin-top: 42px; padding-top: 42px; }
.story-section h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(1.75rem, 4vw, 2.45rem); margin: 0 0 20px; }
.story-section p { color: #c2c2c2; font-size: 1.08rem; line-height: 1.75; margin: 0 0 18px; }
.story-section p:last-child { margin-bottom: 0; }
.description, .empty-occurrences > p:last-child { color: #c2c2c2; font-size: 1.08rem; line-height: 1.75; margin: 0; }
dl { background: #111; border: 1px solid #303030; border-radius: 12px; margin: 0; padding: 8px 22px; }
dl > div { padding: 16px 0; }
dl > div + div { border-top: 1px solid #2c2c2c; }
dt { color: #878787; font-size: .72rem; font-weight: 900; text-transform: uppercase; }
dd { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.2rem; margin: 4px 0 0; }
.section-block { border-top: 1px solid #292929; margin-top: 64px; padding-top: 54px; }
.confirmation-block { align-items: flex-start; background: #151109; border: 1px solid #5f471d; border-radius: 12px; display: grid; gap: 18px; grid-template-columns: auto 1fr; padding: 24px; }
.confirmation-block .v-icon { color: #f0b13c; font-size: 2rem; }
.confirmation-block h2 { font-size: 1.65rem; margin-bottom: 12px; }
.confirmation-block ul { color: #d5c8af; line-height: 1.65; margin: 0; padding-left: 20px; }
.confirmation-block li + li { margin-top: 6px; }
.occurrence-group + .occurrence-group { margin-top: 30px; }
.occurrence-group > h3 { color: #f0b13c; font-family: "Oswald", sans-serif; font-size: 1.5rem; }
.occurrence-grid, .related-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
.location-grid { display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.location-card { background: #141414; border: 1px solid #303030; border-radius: 10px; color: inherit; display: flex; flex-direction: column; justify-content: space-between; min-height: 250px; padding: 24px; text-decoration: none; transition: border-color 180ms ease, transform 180ms ease; }
.location-card:hover, .location-card:focus { border-color: #e23232; transform: translateY(-4px); }
.location-card p { color: #f0b13c; font-family: "Oswald", sans-serif; font-size: .78rem; font-weight: 800; margin: 0 0 7px; text-transform: uppercase; }
.location-card h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.6rem; margin: 0 0 14px; }
.location-card span { color: #b7b7b7; line-height: 1.6; }
.location-card strong { color: #fff; font-size: .82rem; margin-top: 24px; text-transform: uppercase; }
.location-card strong .v-icon { color: #e23232; }
.badge-card { align-items: center; background: #141414; border: 1px solid #303030; border-radius: 10px; display: flex; gap: 16px; max-width: 520px; padding: 22px; }
.badge-card .v-icon { color: #f0b13c; font-size: 2.4rem; }
.badge-card strong { color: #fff; }
.badge-card span { color: #999; font-size: .8rem; margin-left: auto; }
.not-found { color: #fff; min-height: 100vh; padding-top: 50px; }
@media (max-width: 700px) { .detail-grid, .location-grid { grid-template-columns: 1fr; } .badge-card { align-items: flex-start; flex-direction: column; } .badge-card span { margin-left: 0; } }
</style>
